import { useQuery } from "@tanstack/react-query";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import "./Article.scss";
import type { ArticleContentEndpoint } from "../../api/api";
// eslint-disable-next-line no-duplicate-imports
import { getArticleContent } from "../../api/api";
import type { AxiosError } from "axios";
import { Route } from "../../routes/(app)/_app.articles_.$articleId";
import { useRouter } from "@tanstack/react-router";
import { Badge } from "../../components/ui/badge";
// import { format } from "date-fns/format";
import { Skeleton } from "../../components/ui/skeleton";
import { useEffect, useState } from "react";
import { openDB } from "idb";
import { useOnlineStatusContext } from "../../context/OnlineStatusContext";
import placeholderImage from "../../assets/articles/grafika-3.png";
import { getCategoryBadgeStyle } from "@/utils/colorsWorkshop";
import { FEATURE_FLAG_COVER_CARD_NEW_VISUAL } from "@/pages/Home";
import { getArticleCategoryBadgeStyle } from "@/utils/colorsArticle";
import { ExternalHtml } from "@/components/ExternalHtml";

/**
 * Retrieves a single article from IndexedDB by its ID
 * @param articleId The ID of the article to retrieve
 * @returns Promise resolving to the article data or undefined if not found
 */
const getStoredArticleById = async (
	articleId: string
): Promise<ArticleContentEndpoint | undefined> => {
	try {
		// Open the database connection
		const db = await openDB("articles-db", 1);

		// Get the specific article by its ID
		const article = await db.get("articles", Number(articleId));

		// Return the article (will be undefined if not found)
		return article?.content as ArticleContentEndpoint;
	} catch (error) {
		console.error("Error retrieving article from database:", error);
		throw error;
	}
};

const Article = (): JSX.Element => {
	const { articleId } = Route.useParams();
	const router = useRouter();
	const [articleFromDB, setArticleFromDB] =
		useState<ArticleContentEndpoint | null>(null);
	const { isOnline } = useOnlineStatusContext();
	const [imageWithFallback, setImageWithFallback] = useState("");

	const { isLoading, isSuccess, error, data } = useQuery<
		ArticleContentEndpoint,
		AxiosError
	>({
		queryKey: ["articlesContent", Number(articleId)],
		queryFn: () => getArticleContent(Number(articleId)),
		enabled: isOnline,
	});

	if (error) {
		console.error("error", error);
	}

	useEffect(() => {
		const loadArticles = async () => {
			const articleDB = await getStoredArticleById(articleId);
			if (articleDB) {
				setArticleFromDB(articleDB);
			}
		};
		if (!isOnline) {
			loadArticles().catch((error) => {
				console.error("Error loading articles from DB:", error);
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOnline]);

	useEffect(() => {
		if (isSuccess && data) {
			setImageWithFallback(data.image);
		}
	}, [isSuccess, data]);

	// if (data === undefined) {
	// 	return <div>Article not found</div>;
	// }

	return (
		<main className="py-3 px-3 min-h-[calc(100vh-176px)] overflow-x-hidden overflow-scroll">
			{(isSuccess || (!isOnline && !!articleFromDB)) && (
				<>
					<div className="flex text-fontDarkGray">
						<button
							onClick={() => {
								router.history.back();
							}}
						>
							Aktualności
						</button>
						<span className="px-4">&#8226;</span>
						<span className="line-clamp-1">
							{(isOnline ? data : articleFromDB)?.title}
						</span>
					</div>
					<div className="relative pt-4 pb-5">
						<div className="absolute z-10 p-2 flex justify-between w-full gap-2">
							<div className="gap-2">
								{data?.kategorie &&
									Array.isArray((isOnline ? data : articleFromDB)?.kategorie) &&
									data.kategorie.map((cat) => (
										<>
											{FEATURE_FLAG_COVER_CARD_NEW_VISUAL ? (
												<Badge
													key={`${cat.name}${articleId}`}
													className="text-xs font-medium"
													style={getArticleCategoryBadgeStyle(cat.name)}
												>
													{cat.name}
												</Badge>
											) : (
												<Badge
													key={`${cat.name}${articleId}`}
													className="text-xs font-medium"
													variant="highlight"
												>
													{cat.name}
												</Badge>
											)}
										</>
									))}
							</div>
							<div className="flex gap-1">
								{FEATURE_FLAG_COVER_CARD_NEW_VISUAL ? <Badge className="text-xs" variant="semitransparent">
									{(isOnline ? data : articleFromDB)?.author.name}
								</Badge> : <Badge className="text-xs" variant="default">
									{(isOnline ? data : articleFromDB)?.author.name}
								</Badge>
								}
								{data.tagi &&
									Array.isArray(data.tagi) &&
									data.tagi.length > 0 && (
										<div className="pl-2 flex gap-2">
											{data.tagi.map((tag) => (
												<Badge
													key={`${tag.name}${articleId}`}
													variant="articleTag"
												>
													{tag.name.toUpperCase()}
												</Badge>
											))}
										</div>
									)}
							</div>
						</div>
						<div className="absolute z-10 p-2 bottom-4">
							{FEATURE_FLAG_COVER_CARD_NEW_VISUAL ? (
								<Badge className="text-xs" variant="semitransparent">
									{/* {data && format(data.date * 1000, "dd.MM.yyyy")} */}
									{(isOnline ? data : articleFromDB) &&
										(isOnline ? data : articleFromDB).date}
								</Badge>
							) : (
								<Badge className="text-xs" variant="info">
									{/* {data && format(data.date * 1000, "dd.MM.yyyy")} */}
									{(isOnline ? data : articleFromDB) &&
										(isOnline ? data : articleFromDB).date}
								</Badge>
							)}
						</div>
						<LazyLoadImage
							alt="title"
							className="rounded-xl h-[112px] w-full object-cover"
							effect="blur"
							src={imageWithFallback}
							wrapperProps={{
								style: {
									width: "100%",
									zIndex: 0,
								},
							}}
							onError={() => {
								setImageWithFallback(placeholderImage);
							}}
						/>
					</div>
					<h1 className="text-2xl font-bold pb-4 pr-7">
						{(isOnline ? data : articleFromDB)?.title}
					</h1>
					{(isOnline ? data : articleFromDB)?.content && (
						<ExternalHtml
							html={(isOnline ? data : articleFromDB)?.content}
							className="html-content text pr-7"
						/>
					)}
					{(isOnline ? data : articleFromDB) && (
						<>
							<h5 className="text-sm font-medium pb-3">Autor:</h5>
							<div className="flex text-sm">
								<img
									alt="author"
									className="my-auto h-10 w-10 rounded-full"
									src={
										(isOnline ? data : articleFromDB)?.author.profile_picture
									}
								/>
								<div className="ml-3 my-auto">
									<div>{`${(isOnline ? data : articleFromDB)?.author.first_name} ${(isOnline ? data : articleFromDB)?.author.last_name}`}</div>
									<div>
										{(isOnline ? data : articleFromDB)?.author.position}
									</div>
									<a
										href={`mailto:${(isOnline ? data : articleFromDB)?.author.email}`}
									>
										{(isOnline ? data : articleFromDB)?.author.email}
									</a>
								</div>
							</div>
						</>
					)}
				</>
			)}
			{isLoading && (
				<div>
					<Skeleton className="h-[20px] w-full mb-5 rounded-xl" />
					<Skeleton className="h-[112px] w-full mb-5 rounded-xl" />
					<Skeleton className="h-7 w-full mb-2 rounded-xl" />
					<Skeleton className="h-7 w-full mb-5 rounded-xl" />
					<Skeleton className="h-5 w-full mb-3 rounded-xl" />
					<Skeleton className="h-5 w-full mb-3 rounded-xl" />
					<Skeleton className="h-5 w-full mb-3 rounded-xl" />
					<Skeleton className="h-5 w-full mb-3 rounded-xl" />
					<Skeleton className="h-5 w-full mb-3 rounded-xl" />
					<Skeleton className="h-5 w-full mb-3 rounded-xl" />
					<Skeleton className="h-5 w-full mb-3 rounded-xl" />
					<Skeleton className="h-5 w-full mb-3 rounded-xl" />
				</div>
			)}
		</main>
	);
};

export default Article;
